import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { Trophy, Users, AlertTriangle, RefreshCw, Activity } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LeaderboardStats } from "@/components/leaderboard/LeaderboardStats";
import { TraderLeaderboard } from "@/components/leaderboard/TraderLeaderboard";
import { CommunityLeaderboard } from "@/components/leaderboard/CommunityLeaderboard";
import { Achievements } from "@/components/gamification/Achievements";
import { DailyStreak } from "@/components/gamification/DailyStreak";
import { XPProgress } from "@/components/gamification/XPProgress";
import { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";

export default function Leaderboard() {
  const { data: entries, isLoading: entriesLoading, error: entriesError, refetch: refetchEntries } = useQuery({
    queryKey: ["leaderboard"],
    queryFn: async () => {
      const { data, error } = await supabase.from("leaderboard_entries").select("*").order("total_profit", { ascending: false }).limit(50);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: communities, isLoading: communitiesLoading, error: communitiesError, refetch: refetchCommunities } = useQuery({
    queryKey: ["community-leaderboard"],
    queryFn: async () => {
      const { data, error } = await supabase.from("communities").select("*").order("total_profit_sol", { ascending: false }).limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["leaderboard-stats"],
    queryFn: async () => {
      const [tradesResult, usersResult] = await Promise.all([
        supabase.from("trades").select("amount_sol"),
        supabase.from("leaderboard_entries").select("id", { count: "exact" }),
      ]);
      const totalVolume = tradesResult.data?.reduce((sum, t) => sum + (t.amount_sol ?? 0), 0) ?? 0;
      return { volume24h: totalVolume * 200, activeTraders: usersResult.count ?? 0, topProfit: (entries?.[0]?.total_profit ?? 0) * 200, totalTrades: tradesResult.data?.length ?? 0 };
    },
    enabled: !!entries,
  });

  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    if (entries || communities || stats) setLastUpdate(new Date());
  }, [entries, communities, stats]);

  return (
    <>
      <Helmet>
        <title>Leaderboard - TheMoonRun</title>
        <meta name="description" content="Top traders and communities. Compete, earn XP, and climb the ranks." />
      </Helmet>

      <PageLayout>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
            <Trophy className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-500" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-display font-bold text-foreground">Leaderboard</h1>
            <p className="text-sm text-muted-foreground">Compete, earn XP, unlock achievements</p>
            <Badge variant="outline" className="text-xs">
              <Activity className="w-3 h-3 mr-1 animate-pulse" />
              Updated {formatDistanceToNow(lastUpdate, { addSuffix: true })}
            </Badge>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6">
          <LeaderboardStats volume24h={stats?.volume24h ?? 0} activeTraders={stats?.activeTraders ?? 0} topProfit={stats?.topProfit ?? 0} totalTrades={stats?.totalTrades ?? 0} isLoading={entriesLoading} />
        </div>

        {/* Gamification */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2"><DailyStreak /></div>
          <div><XPProgress /></div>
        </div>

        <div className="mb-6"><Achievements /></div>

        {/* Leaderboard Tabs */}
        <Tabs defaultValue="traders" className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="traders" className="gap-2">
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Top</span> Traders
            </TabsTrigger>
            <TabsTrigger value="communities" className="gap-2">
              <Users className="w-4 h-4" />
              Communities
            </TabsTrigger>
          </TabsList>
          <TabsContent value="traders">
            {entriesError ? (
              <div className="flex flex-col items-center justify-center min-h-[40vh]">
                <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
                <h3 className="text-lg font-semibold mb-2">Failed to Load Leaderboard</h3>
                <p className="text-muted-foreground mb-4 text-center max-w-md">
                  {entriesError instanceof Error ? entriesError.message : 'An unexpected error occurred'}
                </p>
                <Button onClick={() => refetchEntries()} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : (
              <TraderLeaderboard entries={entries ?? []} isLoading={entriesLoading} />
            )}
          </TabsContent>
          <TabsContent value="communities">
            {communitiesError ? (
              <div className="flex flex-col items-center justify-center min-h-[40vh]">
                <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
                <h3 className="text-lg font-semibold mb-2">Failed to Load Communities</h3>
                <p className="text-muted-foreground mb-4 text-center max-w-md">
                  {communitiesError instanceof Error ? communitiesError.message : 'An unexpected error occurred'}
                </p>
                <Button onClick={() => refetchCommunities()} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : (
              <CommunityLeaderboard communities={communities ?? []} isLoading={communitiesLoading} />
            )}
          </TabsContent>
        </Tabs>
      </PageLayout>
    </>
  );
}
